import { useState } from 'react';
import { dealNextSteps, formatDateOnly, nextActionBannerKey, nextActionBannerSignature, nextActionBannerText, nextActionLevel, t, type NextActionBannerRow, type WhatsNextStep } from '@bms2/shared';
import { WhatsNextBanner } from '../../components/composites/WhatsNextBanner';
import { useDateFormat, useDocumentToday } from '../../shell/ViewerZone';
import { readPreference, writePreference } from '../../shell/devicePreference';
import { useOpenProgressPanel } from './StagedLayout';

function dismissedFor(key: string): string | null {
  return readPreference<string | null>(key, (raw) => raw, null);
}

/** What a dismissal at Decision is remembered against, where no sub-stage row names the step. */
const DECISION_SIGNATURE = 'Decision|';

export interface DealNextActionBannerProps {
  dealId: string;
  personId: string;
  rows: readonly NextActionBannerRow[];
  /** The Deal's stage, which at Decision offers Won and Lost where no row is left. */
  stage: string | null;
  /**
   * The moves the page can make now, each the ladder's own; Open Deal Progress is always among them.
   * 🔒 Inside the staged layout Open Deal Progress is the layout's own, which opens the panel (T1750).
   */
  actions: Partial<Record<WhatsNextStep, () => void>>;
}

/**
 * 🔒 The Deal page's next action, drawn as the documents' What's next banner
 * (T1272; decision 1935, amending 1219 and 1732): the first open sub-stage named
 * and the rest counted, as decision 1219 words it, with the ladder's own move for
 * that sub-stage as its button, else Open Deal Progress. It has two levels
 * (decision 1485): Urgent, the budget talk or an action whose Due has passed,
 * draws the banner's Warning tone and its words lead *Urgent:*; Normal draws Info.
 * Dismiss, its quiet close, hides it for this person on this Deal in this browser
 * until the first action's name or Due changes (decision 1218).
 *
 * 🔨 It is a visual element and writes nothing anywhere: no History entry, no
 * audit row and no error-log row.
 */
export function DealNextActionBanner({ dealId, personId, rows, stage, actions }: DealNextActionBannerProps) {
  const openPanel = useOpenProgressPanel();
  const format = useDateFormat();
  const today = useDocumentToday();
  const key = nextActionBannerKey(personId, dealId);
  const atDecision = rows.length === 0 && stage === 'Decision';
  const signature = atDecision ? DECISION_SIGNATURE : nextActionBannerSignature(rows);
  // The dismissal read once per key, and held here after a press, so a browser refusing storage still hides it for the visit.
  const [dismissed, setDismissed] = useState<{ key: string; signature: string | null }>(() => ({ key, signature: dismissedFor(key) }));
  const held = dismissed.key === key ? dismissed.signature : dismissedFor(key);
  const text = atDecision ? t('whatsNext.deal.decision') : nextActionBannerText(rows, today, (day) => formatDateOnly(day, format));
  if (!text || !signature || held === signature) return null;
  const dismiss = () => {
    writePreference(key, signature);
    setDismissed({ key, signature });
  };
  const moves: Partial<Record<WhatsNextStep, () => void>> = openPanel && actions.openProgress ? { ...actions, openProgress: openPanel } : actions;
  const offered = (Object.keys(moves) as WhatsNextStep[]).filter((step) => typeof moves[step] === 'function');
  const steps = dealNextSteps(rows[0]?.id ?? null, stage, offered);
  return (
    <div data-deal-next-banner={signature}>
      <WhatsNextBanner documentType="Deal" status={stage} actions={moves} next={{ text, steps }} tone={nextActionLevel(rows, today) === 'urgent' ? 'warning' : 'info'} onDismiss={dismiss} />
    </div>
  );
}
